import { ButtonVariants } from '../../constants'

import { FormEvent, useMemo, useState } from 'react'

import { createClient } from '@supabase/supabase-js'
import classNames from 'classnames'
import RoundedButton from 'components/Atoms/Buttons/RoundedButton/RoundedButton'
import TextInput from 'components/Atoms/Register/TextInput'
import Header from 'components/Organisms/Header'
import { useTranslation } from 'react-i18next'

type Category = 'error' | 'feature' | 'confusing' | 'support' | 'other'

type ViewState = 'form' | 'success' | 'submitError'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''
const supabase = createClient(supabaseUrl, supabaseKey)

export default function Feedback() {
  const { t } = useTranslation('feedback')

  const categories = useMemo<{ id: Category; label: string }[]>(
    () => [
      { id: 'error', label: t('categories.error') },
      { id: 'confusing', label: t('categories.confusing') },
      { id: 'feature', label: t('categories.feature') },
      { id: 'support', label: t('categories.support') },
      { id: 'other', label: t('categories.other') },
    ],
    [t],
  )

  const [category, setCategory] = useState<Category | ''>('')
  const [comment, setComment] = useState('')
  const [email, setEmail] = useState('')
  const [viewState, setViewState] = useState<ViewState>('form')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canSubmit =
    Boolean(category) && comment.trim().length > 0 && !isSubmitting

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    setIsSubmitting(true)

    try {
      const { error } = await supabase.from('user_feedback').insert([
        {
          category,
          comment,
          email: email.trim().length > 0 ? email.trim() : null,
        },
      ])

      if (error) {
        throw error
      }

      setViewState('success')
    } catch (error) {
      console.error('Error al guardar el feedback:', error)
      setViewState('submitError')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (viewState === 'success') {
    return (
      <>
        <Header
          logo="logo_10years.png"
          onClick={() => {}}
          hideOptions
          hideButton
        />
        <div className="min-h-[calc(100vh-80px)] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white md:shadow sm:rounded-lg overflow-hidden">
              <div className="bg-black text-white px-6 py-8 relative overflow-hidden">
                <div className="relative z-10">
                  <h1 className="text-xl font-semibold text-center">
                    {t('success.title')}
                  </h1>
                  <p className="text-center text-gray-300 mt-2">
                    {t('success.subtitle')}
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full -mr-24 -mt-24 blur-2xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24 blur-2xl" />
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (viewState === 'submitError') {
    const errorKey = 'errors.submit'

    return (
      <>
        <Header
          logo="logo_10years.png"
          onClick={() => {}}
          hideOptions
          hideButton
        />
        <div className="min-h-[calc(100vh-80px)] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white md:shadow sm:rounded-lg overflow-hidden">
              <div className="bg-black text-white px-6 py-8 relative overflow-hidden">
                <div className="relative z-10">
                  <h1 className="text-xl font-semibold text-center">
                    {t(`${errorKey}.title`)}
                  </h1>
                  <p className="text-center text-gray-300 mt-2">
                    {t(`${errorKey}.subtitle`)}
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full -mr-24 -mt-24 blur-2xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24 blur-2xl" />
              </div>

              <div className="px-6 py-8 sm:px-10">
                <RoundedButton
                  type="button"
                  variant={ButtonVariants.PRIMARY}
                  className="w-full"
                  onClick={() => setViewState('form')}
                >
                  {t('form.submit')}
                </RoundedButton>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Header
        logo="logo_10years.png"
        onClick={() => {}}
        hideOptions
        hideButton
      />
      <div className="min-h-[calc(100vh-80px)] bg-gray-50 flex flex-col justify-center max-sm:pb-12 sm:py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
          <div className="bg-white md:shadow sm:rounded-lg overflow-hidden">
            <div className="bg-black text-white px-6 py-10 sm:px-10 relative overflow-hidden">
              <div className="relative z-10">
                <h1 className="mt-4 text-2xl sm:text-3xl font-bold">
                  {t('title')}
                </h1>
                <p className="mt-3 text-gray-300">{t('subtitle')}</p>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/10 rounded-full -ml-28 -mb-28 blur-3xl" />
            </div>

            <form
              onSubmit={handleSubmit}
              className="px-6 py-8 sm:px-10 space-y-6"
              noValidate
            >
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.categoryLabel')}
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map(cat => {
                    const selected = category === cat.id
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategory(cat.id)}
                        className={classNames(
                          'w-full text-left px-4 py-3 rounded-lg border shadow-sm transition',
                          {
                            'border-blue-800 ring-2 ring-blue-800': selected,
                            'border-gray-300 hover:border-gray-400': !selected,
                          },
                        )}
                      >
                        <span className="font-semibold text-sm text-gray-900">
                          {cat.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
                {!category && (
                  <p className="text-xs text-gray-500 mt-2">
                    {t('form.categoryHint')}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="feedback-comment"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('form.commentLabel')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  id="feedback-comment"
                  required
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder={t('form.commentPlaceholder')}
                  className="mt-2 block w-full min-h-[160px] px-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm resize-none"
                />
              </div>

              <div>
                <TextInput
                  label={t('form.emailLabel')}
                  type="email"
                  placeholder={t('form.emailPlaceholder')}
                  value={email}
                  onChange={setEmail}
                  required={false}
                />
                <p className="text-xs text-gray-500 -mt-2">
                  {t('form.emailHelp')}
                </p>
              </div>

              <RoundedButton
                type="submit"
                variant={ButtonVariants.PRIMARY}
                className="w-full"
                disabled={!canSubmit || isSubmitting}
              >
                {t('form.submit')}
              </RoundedButton>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
